import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { getAllAdminUsers, getFarmVerifications } from "services";

import { getAdminVerificationYearlyDataYear, useAppType, useNavigate } from "utils";

import { DEFAULT_CACHE, DEFAULT_SHORT_CACHE, GET_ADMIN_USERS, GET_FARM_VERIFICATIONS } from "consts";

import { AdminHead, Col, ErrorBoundary, FilterSet, Flex, Icon, InputSearch, MainPage, Row, Switch, Table, TablePanel, Tag, Tooltip } from "components";

import { SelectAdminContextMenu } from "pages/admin/verifications/components/misc/farmList/SelectAdminContextMenu";
import { TableSubPanels } from "pages/admin/verifications/components/misc/farmList/TableSubPanels";
import { VerifyButton } from "pages/admin/verifications/components/misc/farmList/VerifyButton";

type QueryFilter = string[];

export const FarmList = () => {
  const [expandedRows, setExpandedRows] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<QueryFilter>([]);
  const [includeAll, setIncludeAll] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const defaultItemsPerPage = 20;
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  const appType = useAppType();
  const navigate = useNavigate();

  const verificationYear = getAdminVerificationYearlyDataYear();

  const updateSearch = (s: string) => {
    clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        setSearch(s.length ? s : "");
      }, 1000)
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: [GET_FARM_VERIFICATIONS, filters[0], filters[1], search, page, itemsPerPage, includeAll],
    queryFn: () =>
      getFarmVerifications({
        filter: filters[0] ?? undefined,
        assigneeId: filters[0] === "withAssignee" ? filters[1] : undefined,
        search,
        page: page - 1,
        itemsPerPage,
        includeAll,
      }),
    // Short cache so we don't use yesterday evening's results the following morning
    ...DEFAULT_SHORT_CACHE,
  });

  const { data: adminUsers } = useQuery({
    queryKey: [GET_ADMIN_USERS, "verifier"],
    queryFn: () => getAllAdminUsers({ verifier: true }),
    ...DEFAULT_CACHE,
  });

  const handlePageChange = (page: number, pageSize: number) => {
    setPage(page);
    setItemsPerPage(pageSize);
  };

  const handleSelectFilter = (key: string) => {
    setFilters([...filters, key]);
    setPage(1);
  };

  const handleUnselectFilter = (i: number) => {
    setFilters(i === 0 ? [] : [filters[0]]);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const filterSettings = {
    selectedFilters: [...filters],
    onClearFilters: handleClearFilters,
    onSelectFilter: handleSelectFilter,
    onUnselectFilter: handleUnselectFilter,
    filters: {
      withAssignee: {
        title: "with assignee",
        // Convert admin users array into object
        filters: Object.fromEntries(adminUsers?.map(({ id, name }) => [id, { title: name }]) ?? []),
      },
    },
  };

  const tableData = data?.farms.map((farm) => {
    const { id, name, reviewerId, fieldVerificationsCounts } = farm;
    const reviewer = adminUsers?.find((admin) => admin.id === reviewerId);

    let farmState = fieldVerificationsCounts.rejectedFieldCount + fieldVerificationsCounts.verifiedFieldCount + 1;

    let tag = null;

    if (farm.taskStatus === "pending_verifications" || (farm.taskStatus === "no_tasks" && farm.verified)) {
      tag = <Tag colour="yellow" text="pending verification" />;
    }

    if (farm.taskStatus === "missing_documents") {
      tag = <Tag colour="red" text="missing documentation" />;
    }

    if (farm.verified === null) {
      farmState -= 1;
    }

    const farmVerifications = {
      color: "blue",
      text: "ready to approve",
      icon: "tabler/alert-triangle",
    };

    if (farm.verified === null || fieldVerificationsCounts.unverifiedFieldCount > 0) {
      farmVerifications.color = "grey-600";
      farmVerifications.text = "not verifed";
      farmVerifications.icon = "tabler/help";
    }

    const currentVerificationYearApproval = farm.payoutApprovals?.find(({ year }) => year === verificationYear);

    if (currentVerificationYearApproval) {
      const { approved } = currentVerificationYearApproval;

      tag = null;
      farmVerifications.color = approved ? "green" : "red";
      farmVerifications.text = approved ? "approved" : "rejected";
      farmVerifications.icon = approved ? "tabler/circle-check" : "tabler/circle-x";
    }
    const fieldVerificationsCount =
      fieldVerificationsCounts.verifiedFieldCount + fieldVerificationsCounts.rejectedFieldCount + fieldVerificationsCounts.unverifiedFieldCount;

    return {
      key: id,
      // @ts-expect-error TablePanel not yet in TS
      "farm name": <TablePanel className="farms-panel" headline={name} tag={tag} />,
      size: <span className="paragraph-xs">{farm.size}</span>,
      progress: (
        <div>
          {farmState}/{fieldVerificationsCount + 1}
        </div>
      ),
      status: (
        <div className={farmVerifications.color}>
          <Icon iconName={farmVerifications.icon} className="icon inline margin-right-25" />
          <span>{farmVerifications.text}</span>
        </div>
      ),
      admin: <SelectAdminContextMenu reviewer={reviewer} adminUsers={adminUsers ?? []} reviewerId={reviewerId} farmId={farm.id} />,
      expandable: (
        <ErrorBoundary>
          <div className="margin-top-200">
            <TableSubPanels farm={farm} />
          </div>
          <VerifyButton farm={farm} />
        </ErrorBoundary>
      ),
      " ": <Icon className="icon inline" iconName={`tabler/chevron-${expandedRows.includes(id) ? "up" : "down"}`} />,
    };
  });

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <AdminHead
        headline="Verifications"
        extra={
          <Tooltip placement="left" title="Verification task settings">
            <Icon iconName="tabler/settings" className="icon inline pointer" size="m" onClick={() => navigate("/verifications/tasks")} />
          </Tooltip>
        }
      />
      <div className="verification-farms">
        <h3 className="margin-bottom-150">{data?.count} farms</h3>
        <Row>
          <Col>
            <Flex className="padding-100" vertical gap={16}>
              <InputSearch label={"farms search"} onChange={(e) => updateSearch(e.target.value)} />
              <Flex gap={16}>
                Include all farms
                <Switch value={includeAll} onChange={(v) => setIncludeAll(v)} />
              </Flex>
              <FilterSet {...filterSettings} />
            </Flex>
            <Row>
              <Col>
                {!isLoading && data?.count === 0 && <p>There are no farms to verify.</p>}
                <Table
                  data={tableData ?? []}
                  className={"farms"}
                  pagination={{
                    total: data?.count,
                    defaultPageSize: defaultItemsPerPage,
                    pageSize: itemsPerPage,
                    onChange: handlePageChange,
                  }}
                  loading={isLoading}
                  expandable={{
                    onExpandedRowsChange: (ks) => setExpandedRows([...ks]),
                    expandedRowRender: (item) => <>{item.expandable}</>,
                  }}
                  hideHeader={appType.mobile}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </MainPage>
  );
};
